import {
    ChangeEvent,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import AddOrg from './AddOrg'
import {
    Box,
    Button,
    Grid,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Switch,
    FormControlLabel,
} from '@mui/material'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import {
    MRT_RowSelectionState,
    MRT_ColumnFiltersState,
    MRT_SortingState,
    MRT_ColumnDef,
} from 'material-react-table'
import { deleteOrg, fetchOrgs } from '../../services/orgServices'
import MRTTable from '../Common/Table'
import SearchIcon from '@mui/icons-material/Search'
import { enqueueSnackbar } from 'notistack'
import { useAppSelector } from '../../redux/hook'
import theme from '../../theme/theme'
import {
    IOrgformData,
    Org,
    IOpenAddOrEditOrg,
    OrgApiResponse,
} from './interfaces'
import ConfirmationDialog from '../Common/ConfirmationDialog'
import { ORG_ADD_MODE } from './constants'
import ShareOrg from './ShareOrg'
import { ShareOutlined } from '@mui/icons-material'
import {
    defaultFormData,
    ReportTypes,
    UserPermissions,
} from '../../constants/constants'
import { formatDateTimeWithUserPreferences } from '../../utils/DateAndTimeUtils'
import { useTranslation } from 'react-i18next'
import { useRoutePermission } from '../../hooks/useRoutePermission'
import { usePermissions } from '../../hooks/usePermissions'
import useDebounce from '../../CustomHooks/useDebounce'

const fetchSize = 30
const actionColumnSize = 35

const actionCellSx = {
    backgroundColor: '#E9F6FF',
    borderBottom: '3px solid #FFFFFF',
    borderTop: '3px solid #FFFFFF',
    fontSize: '14px',
    padding: '0px',
    textAlign: 'center',
}

const actionIconButtonSx = {
    backgroundColor: 'transparent',
    height: 28,
    padding: '2px',
    width: 28,
    '&:hover': {
        backgroundColor: 'transparent',
    },
}

const displayColumnDefOptions = {
    'mrt-row-select': {
        header: '',
        size: actionColumnSize,
        minSize: actionColumnSize,
        grow: false,
        maxSize: actionColumnSize,
        muiTableBodyCellProps: {
            sx: {
                ...actionCellSx,
                '& .MuiCheckbox-root': {
                    padding: '2px',
                },
            },
        },
        muiTableHeadCellProps: {
            sx: {
                padding: '0px',
                textAlign: 'center',
            },
        },
    },
}

const Organizations = (): ReactElement => {
    const { t } = useTranslation()
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )

    const org = useAppSelector((state) => state.org.org)
    const orgId = org?.id || ''
    //checking the permissions, if user doesn't have permission. redirecting to home
    useRoutePermission([
        UserPermissions.ORG_ADMIN,
        UserPermissions.SYSTEM_READER,
    ])
    const { hasPermission } = usePermissions()
    const hasWriteAccess = useMemo(() => {
        return hasPermission(UserPermissions.ORG_ADMIN)
    }, [hasPermission])

    const addBtnRef = useRef<HTMLDivElement | null>(null)
    const [openAddOrEditOrg, setOpenAddOrEditOrg] = useState<IOpenAddOrEditOrg>(
        {
            mode: ORG_ADD_MODE.ADD,
            open: false,
            formData: defaultFormData,
        },
    )
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
        [],
    )

    const [globalFilter, setGlobalFilter] = useState<string>()
    const debouncedGlobalFilter = useDebounce(globalFilter, 700)
    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

    const [isAddOptionsOpen, setIsAddOptionsOpen] = useState<boolean>(false)

    const [orgsToDelete, setOrgsToDelete] = useState<string[]>([])

    const [isShareOpen, setIsShareOpen] = useState<boolean>(false)
    const [shareOrgId, setShareOrgId] = useState<string>('')

    const [displayVirtualOrgs, setDisplayVirtualOrgs] = useState<boolean>(false)

    const queryClient = useQueryClient()

    const resetAddOrEditOrgState = useCallback(() => {
        setOpenAddOrEditOrg({
            mode: ORG_ADD_MODE.ADD,
            open: false,
            formData: defaultFormData,
        })
    }, [])

    const handleAddOrEditOrg = useCallback(
        (refreshTable: boolean) => {
            if (refreshTable) {
                queryClient.invalidateQueries({
                    queryKey: ['organizations'],
                })
                queryClient.invalidateQueries({
                    queryKey: ['organizations-table-data'],
                })
                setRowSelection({})
            }
            resetAddOrEditOrgState()
        },
        [queryClient, resetAddOrEditOrgState],
    )

    const handleOpen = useCallback((mode: string, data: IOrgformData) => {
        setOpenAddOrEditOrg({
            mode,
            open: true,
            formData: data,
        })
    }, [])

    const normalizedSort = useMemo(() => {
        let filterBy = sorting[0]?.id
        const sortOrder = filterBy ? (sorting[0]?.desc ? 'DESC' : 'ASC') : ''

        if (filterBy === 'userCount') {
            filterBy = 'USER'
        }
        if (filterBy === 'Created At') {
            filterBy = 'CREATED'
        }
        if (filterBy === 'parentOrganizationName') {
            filterBy = 'PARENT_ORG'
        }

        return {
            filterBy: filterBy ? filterBy.toUpperCase() : '',
            sortOrder,
        }
    }, [sorting])

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<OrgApiResponse>({
            queryKey: [
                'organizations-table-data',
                columnFilters, // refetch when columnFilters changes
                debouncedGlobalFilter, // refetch when debouncedGlobalFilter changes
                sorting, // refetch when sorting changes
                displayVirtualOrgs,
            ],
            queryFn: async ({ pageParam }) => {
                const response = await fetchOrgs(
                    {
                        pageNumber: (pageParam as number) + 1,
                        size: fetchSize,
                        searchText: debouncedGlobalFilter?.trim() ?? '',
                        filterBy: normalizedSort.filterBy,
                        sortOrder: normalizedSort.sortOrder,
                        includeVirtualOrgs: displayVirtualOrgs,
                    },
                    /**
                     * Try to remove ! at later point of time.
                     */
                    orgId,
                )
                return response
            },
            initialPageParam: 0,
            getNextPageParam: (lastPage, pages) => {
                // Check if we got any data
                if (!lastPage?.organizations?.length) {
                    return undefined
                }

                // Calculate if we've received all data
                const totalFetched = pages.reduce(
                    (acc, page) => acc + (page?.organizations?.length || 0),
                    0,
                )
                if (totalFetched >= (lastPage?.meta?.totalRowCount || 0)) {
                    return undefined
                }

                return pages.length
            },
            refetchOnWindowFocus: false,
        })

    const flatData = useMemo(
        () => data?.pages.flatMap((page) => page.organizations) ?? [],
        [data],
    )

    const totalDBRowCount = useMemo(
        () => data?.pages?.[0]?.meta?.totalRowCount ?? 0,
        [data],
    )

    const handleClickOutsideAddOrg = useCallback((event: MouseEvent) => {
        /**
         * TODO:
         *
         * This code block closes the menu dropdown that opens when we click on Add Org button. The dropdown has
         * options like Enterprize organization, organization. However this Enterprize organization is commented out as of now.
         * If we are never going to have the functionlity we should remove this relevant code block.
         */
        const container = addBtnRef.current ?? document.body // Fallback to body
        const target = event.target

        if (target instanceof Node && container.contains(target)) {
            // nothing
        } else {
            setIsAddOptionsOpen(false)
        }
    }, [])

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutsideAddOrg)
        return () =>
            document.removeEventListener('mousedown', handleClickOutsideAddOrg)
    }, [handleClickOutsideAddOrg])

    const onAddEnterpriceOrg = useCallback(() => {
        setIsAddOptionsOpen(false)
        handleOpen(ORG_ADD_MODE.ADD_ENTERPRICE, { ...defaultFormData })
    }, [handleOpen])

    const onAddNormalOrg = useCallback(() => {
        setIsAddOptionsOpen(false)
        handleOpen(ORG_ADD_MODE.ADD, { ...defaultFormData })
    }, [handleOpen])

    const openEditOrg = useCallback(
        (rowData: Org) => {
            if (!rowData) {
                return
            }

            const whichEdit = rowData?.isEnterpriseOrg
                ? ORG_ADD_MODE.EDIT_ENTERPRICE
                : ORG_ADD_MODE.EDIT
            handleOpen(whichEdit, {
                id: rowData.id,
                orgId: rowData.orgId,
                parentOrganizationId: rowData.parentOrganizationId,
                name: rowData.name,
                description: rowData.description,
                applicationAccess: rowData.applicationAccess,
                preferences: {
                    ...rowData.preferences.data,
                    token: rowData.preferences?.data?.token,
                    isSync: rowData.preferences?.data?.isSync,
                    syncStartDate: rowData.preferences?.data?.syncStartDate,
                    handGrenade:
                        rowData.preferences?.data?.handGrenade === 'true' ||
                        rowData.preferences?.data?.handGrenade === true ||
                        false,
                    deviceAutoPowerOff:
                        rowData.preferences?.data?.deviceAutoPowerOff ===
                            'true' ||
                        rowData.preferences?.data?.deviceAutoPowerOff ===
                            true ||
                        false,
                    reportType:
                        rowData.preferences?.data?.reportType ??
                        ReportTypes[0].id,
                    fixTemperatureLocationDrift:
                        rowData.preferences?.data
                            ?.fixTemperatureLocationDrift === 'true' ||
                        rowData.preferences?.data
                            ?.fixTemperatureLocationDrift === true ||
                        false,
                },
                orgUnits: rowData?.orgUnits?.units,
                accountId: rowData?.accountId,
                localization: rowData?.preferences?.localization,
                insuranceDetails: rowData?.preferences?.insuranceDetails || [],
                defaultProduct: rowData?.preferences?.defaultProduct || null,
                defaultShipper: rowData?.preferences?.defaultShipper || null,
                defaultReceiver: rowData?.preferences?.defaultReceiver || null,
            })
        },
        [handleOpen],
    )

    const deleteOrgHandler = useCallback(() => {
        const keys = Object.keys(rowSelection)
        const nextOrgsToDelete: string[] = []

        for (const keyStr of keys) {
            const key = Number(keyStr)
            const rowData: Org = flatData[key]

            if (rowData.userCount > 0) {
                enqueueSnackbar(t('cannotDeleteOrgWithActiveUsers'), {
                    variant: 'error',
                })
                return
            }

            nextOrgsToDelete.push(rowData.id)
        }

        setOrgsToDelete(nextOrgsToDelete)
        setOpenDeleteDialog(true)
    }, [flatData, rowSelection, t])

    const handleDeleteDialogOperation = useCallback(async () => {
        /**
         * Try to remove ! at later point of time.
         */
        await deleteOrg(orgsToDelete, orgId)
        setRowSelection({})
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: ['organizations'],
            }),
            queryClient.invalidateQueries({
                queryKey: ['organizations-table-data'],
            }),
        ])
        setOpenDeleteDialog(false)
    }, [orgId, orgsToDelete, queryClient])

    const shareOrgHandler = useCallback((rowData: Org) => {
        if (!rowData) {
            return
        }

        setShareOrgId(rowData.id)
        setIsShareOpen(true)
    }, [])

    const onCloseSharePopup = useCallback(() => {
        setShareOrgId('')
        setIsShareOpen(false)
    }, [])

    const disableOptions = useMemo(
        () => Object.keys(rowSelection).length === 0,
        [rowSelection],
    )

    const handleDisplayVirtualOrgsChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setDisplayVirtualOrgs(event.target.checked)
        },
        [],
    )

    const handleGlobalFilterChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            setGlobalFilter(event.target.value)
        },
        [],
    )

    const handleCloseDeleteDialog = useCallback(() => {
        setOpenDeleteDialog(false)
    }, [])

    const handleCloseShareOrg = useCallback(() => {
        onCloseSharePopup()
    }, [onCloseSharePopup])

    const columns: MRT_ColumnDef<Org>[] = useMemo(
        () => [
            {
                header: '',
                accessorKey: 'editid',
                id: 'editid',
                Cell: ({ row }) => (
                    <IconButton
                        id={`row-${row.id}-edit-button`}
                        onClick={(event) => {
                            event.stopPropagation()
                            openEditOrg(row.original)
                        }}
                        aria-controls="simple-menu-preview"
                        aria-haspopup="true"
                        aria-label={
                            hasWriteAccess
                                ? t('editOrganization')
                                : t('viewOrganization')
                        }
                        sx={actionIconButtonSx}
                        title={
                            hasWriteAccess
                                ? t('editOrganization')
                                : t('viewOrganization')
                        }
                    >
                        {hasWriteAccess ? (
                            <img
                                src="/assets/images/icons/edit.svg"
                                alt={t('editOrganization')}
                            />
                        ) : (
                            <img
                                src="/assets/images/icons/preview.svg"
                                alt={t('viewOrganization')}
                            />
                        )}
                    </IconButton>
                ),
                size: actionColumnSize,
                minSize: actionColumnSize,
                grow: false,
                maxSize: actionColumnSize,
                enableResizing: false,
                enableSorting: false,
                enableColumnFilter: false,
                muiTableBodyCellProps: {
                    role: 'gridcell',
                    sx: actionCellSx,
                },
            },
            {
                header: '',
                accessorKey: 'shareid',
                id: 'shareid',
                Cell: ({ row }) => (
                    <IconButton
                        id={`row-${row.id}-share-button`}
                        onClick={(event) => {
                            event.stopPropagation()
                            shareOrgHandler(row.original)
                        }}
                        aria-controls="simple-menu-share"
                        aria-haspopup="true"
                        aria-label={t('shareWithOrganization')}
                        sx={actionIconButtonSx}
                        title={t('shareWithOrganization')}
                    >
                        <ShareOutlined color="primary" fontSize="small" />
                    </IconButton>
                ),
                size: actionColumnSize,
                minSize: actionColumnSize,
                grow: false,
                maxSize: actionColumnSize,
                enableResizing: false,
                enableSorting: false,
                enableColumnFilter: false,
                muiTableBodyCellProps: {
                    role: 'gridcell',
                    sx: actionCellSx,
                },
            },
            {
                accessorKey: 'name',
                header: t('name'),
                muiTableBodyCellProps: { role: 'gridcell' },
            },
            {
                accessorKey: 'description',
                header: t('description'),
                muiTableBodyCellProps: { role: 'gridcell' },
            },
            {
                accessorKey: 'userCount',
                header: t('totalUsers'),
                muiTableBodyCellProps: { role: 'gridcell' },
            },
            {
                accessorFn: (row) => {
                    if (row.createdAt) {
                        return formatDateTimeWithUserPreferences(
                            row.createdAt,
                            userLocalization,
                        )
                    } else {
                        return ''
                    }
                },
                header: t('createdAt'),
                enableFilterMatchHighlighting: false,
                muiTableBodyCellProps: { role: 'gridcell' },
            },
            {
                accessorKey: 'parentOrganizationName',
                header: t('enterpriseOrganizationText'),
                muiTableBodyCellProps: { role: 'gridcell' },
            },
        ],
        [hasWriteAccess, openEditOrg, shareOrgHandler, t, userLocalization],
    )

    return (
        <Grid
            sx={{
                margin: '30px',
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '20px',
                maxHeight: `calc(100vh - 199px)`,
                overflow: 'auto',
            }}
        >
            <Grid
                container
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 3,
                }}
            >
                <Grid
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h4" component="h2">
                        {t('organizations')}
                    </Typography>

                    <Box sx={{ marginLeft: '30px' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    id="displayVirtualOrgs"
                                    checked={displayVirtualOrgs}
                                    onChange={handleDisplayVirtualOrgsChange}
                                    name="Virtual Organizations"
                                />
                            }
                            label={t('virtualOrganizations')}
                            sx={{
                                marginRight: '0px',
                                '& .MuiFormControlLabel-label': {
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                },
                            }}
                        ></FormControlLabel>
                    </Box>
                </Grid>
                <Grid
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            background: '#E3F4FF',
                            borderRadius: '25px',
                            marginRight: '10px',
                        }}
                    >
                        <TextField
                            id="organizations-search"
                            sx={{
                                padding: 0,
                                '& fieldset': { border: 'none' },
                            }}
                            placeholder={t('search')}
                            value={globalFilter}
                            onChange={handleGlobalFilterChange}
                            className="table-search-bar"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            aria-hidden="true"
                                            tabIndex={-1}
                                            sx={{
                                                '&:hover ': {
                                                    background: 'none',
                                                    cursor: 'auto',
                                                    color: '#00797a',
                                                },
                                            }}
                                        >
                                            <SearchIcon
                                                sx={{ color: 'primary' }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                style: {
                                    height: 36,
                                    paddingLeft: '5px',
                                },
                            }}
                        />
                    </Box>
                    {hasWriteAccess ? (
                        <>
                            <Box
                                sx={{
                                    cursor: disableOptions
                                        ? 'not-allowed'
                                        : 'default',
                                }}
                            >
                                <Button
                                    onClick={deleteOrgHandler}
                                    disabled={disableOptions}
                                    sx={{
                                        minWidth: '25px',
                                        maxWidth: '30px',
                                        marginLeft: '5px',
                                        filter: disableOptions
                                            ? 'grayscale(100%)'
                                            : 'none',
                                        opacity: disableOptions ? 0.5 : 1,
                                    }}
                                    id="delete-org-button"
                                    aria-label={t('deleteOrganization')}
                                    title={t('deleteOrganization')}
                                >
                                    <img
                                        src={`/assets/images/icons/delete-icon.svg`}
                                        alt={t('deleteOrganization')}
                                    />
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    position: 'relative',
                                }}
                                ref={addBtnRef}
                            >
                                <Button
                                    // onClick={openAddOrg}
                                    onClick={onAddNormalOrg}
                                    sx={{
                                        minWidth: '25px',
                                        maxWidth: '30px',
                                        marginLeft: '15px',
                                        minHeight: '25px',
                                        maxHeight: '30px',
                                        marginRight: '10px',
                                    }}
                                    id="add-org-button"
                                    aria-label={t('addOrganization')}
                                    title={t('addOrganization')}
                                >
                                    <img
                                        src={`/assets/images/icons/add-icon.svg`}
                                        alt="Add organization"
                                        aria-label={t('addOrganization')}
                                        title={t('addOrganization')}
                                    />
                                </Button>
                                {isAddOptionsOpen ? (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            background: '#fff',
                                            top: 0,
                                            right: '60px',
                                            padding: '15px',
                                            zIndex: 110,
                                            boxShadow:
                                                '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'flex-start',
                                            gap: '10px',
                                        }}
                                    >
                                        <Box
                                            sx={{ cursor: 'pointer' }}
                                            onClick={onAddEnterpriceOrg}
                                            id="add-enterprise-org-button"
                                        >
                                            <Typography variant="h5" noWrap>
                                                {t(
                                                    'enterpriseOrganizationText',
                                                )}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{ cursor: 'pointer' }}
                                            onClick={onAddNormalOrg}
                                            id="add-normal-org-button"
                                        >
                                            <Typography variant="h5">
                                                {t('organization')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : null}
                            </Box>{' '}
                        </>
                    ) : null}
                    {openAddOrEditOrg.open && (
                        <AddOrg
                            openAddOrEditOrg={openAddOrEditOrg}
                            handleAddOrEditOrg={handleAddOrEditOrg}
                            readOnly={!hasWriteAccess}
                        />
                    )}
                    {}
                </Grid>
            </Grid>
            <MRTTable<Org, OrgApiResponse>
                key={JSON.stringify(userLocalization) || 'default'}
                columns={columns}
                sorting={sorting}
                globalFilter={globalFilter}
                columnFilters={columnFilters}
                setRowSelection={setRowSelection}
                rowSelection={rowSelection}
                setColumnFilters={setColumnFilters}
                setGlobalFilter={setGlobalFilter}
                setSorting={setSorting}
                data={flatData}
                totalDBRowCount={totalDBRowCount}
                fetchNextPage={fetchNextPage}
                isError={isError}
                isFetching={isFetching}
                isLoading={isLoading}
                enableBottomToolbar={true}
                isCurvedBottomToolbar={true}
                enableRowSelection={hasWriteAccess}
                displayColumnDefOptions={displayColumnDefOptions}
                maxHeight="calc(100vh - 348px)"
            />
            <ConfirmationDialog
                openDialog={openDeleteDialog}
                handleComfirm={handleDeleteDialogOperation}
                handleClose={handleCloseDeleteDialog}
                DialogHeaderText={t('confirmDelete')}
                DialogBodyText={t('deleteOrgsConfirmation')}
            />
            {isShareOpen && (
                <ShareOrg
                    seletedOrg={shareOrgId}
                    isOpen={isShareOpen}
                    onClose={handleCloseShareOrg}
                    readOnly={!hasWriteAccess}
                />
            )}
        </Grid>
    )
}

export default Organizations
